// src/components/PseudoAnonymizerPage/DePseudoStatsPanel.jsx
import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, Collapse, Divider, Stack, Typography, Button } from '@mui/material';

const tokenIntegrityPct = (tokensFound, mappingTotal) => {
  const tf = typeof tokensFound === 'number' ? tokensFound : null;
  const mt = typeof mappingTotal === 'number' ? mappingTotal : null;
  if (tf === null || mt === null || mt <= 0) return null;
  const v = (tf / mt) * 100;
  if (!Number.isFinite(v)) return null;
  return `${v.toFixed(0)}%`;
};

/**
 * Expected stats shape (example):
 * {
 *   scopes_scanned: ["paragraphs"],
 *   mapping_total: 10,
 *   tokens_found: 7,
 *   tokens_not_found: 3,
 *   occurrences_found_total: 25,
 *   occurrences_replaced_total: 25,
 *   not_found_tokens: ["CODE1","CODE2"],
 *   truncated: true
 * }
 */
const DePseudoStatsPanel = ({ stats, t }) => {
  const [showDetails, setShowDetails] = useState(false);

  const normalized = useMemo(() => {
    if (!stats) return null;

    // allow service to pass either `stats` only, or a mixed object
    const mappingTotalRaw = stats.mapping_total ?? stats.mappingTotal ?? 0;
    const tokensFoundRaw = stats.tokens_found ?? stats.tokensFound ?? null;
    const tokensNotFoundRaw = stats.tokens_not_found ?? stats.tokensNotFound ?? null;

    const mappingTotal = typeof mappingTotalRaw === 'number' ? mappingTotalRaw : Number(mappingTotalRaw || 0);

    const tokensNotFound = typeof tokensNotFoundRaw === 'number' ? tokensNotFoundRaw : null;

    // derive if missing
    const derivedTokensFound =
      typeof tokensFoundRaw === 'number'
        ? tokensFoundRaw
        : (mappingTotal && typeof tokensNotFoundRaw === 'number' ? Math.max(mappingTotal - tokensNotFoundRaw, 0) : null);

    const tokensFound = typeof derivedTokensFound === 'number' ? derivedTokensFound : null;

    const occFound = stats.occurrences_found_total ?? stats.occurrencesFoundTotal ?? null;
    const occReplaced = stats.occurrences_replaced_total ?? stats.occurrencesReplacedTotal ?? stats.replacedCount ?? null;

    const notFoundTokens = Array.isArray(stats.not_found_tokens) ? stats.not_found_tokens : [];
    const truncated = !!stats.truncated;

    const integrity = tokenIntegrityPct(tokensFound, mappingTotal);

    return {
      mappingTotal,
      tokensFound,
      tokensNotFound,
      occFound: typeof occFound === 'number' ? occFound : null,
      occReplaced: typeof occReplaced === 'number' ? occReplaced : null,
      notFoundTokens,
      truncated,
      integrity,
    };
  }, [stats]);

  if (!normalized) return null;

  const { mappingTotal, tokensFound, tokensNotFound, occFound, occReplaced, notFoundTokens, truncated, integrity } = normalized;

  return (
    <Alert severity="info" sx={{ mb: 1 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Typography variant="subtitle2">
            {t?.('DePseudoStatsPanel.title', 'Štatistiky integrity') ?? 'Štatistiky integrity'}
          </Typography>

          {integrity && (
            <Chip
              size="small"
              label={`${t?.('DePseudoStatsPanel.integrity', 'Integrita') ?? 'Integrita'}: ${integrity}`}
            />
          )}

          {truncated && (
            <Chip
              size="small"
              label={t?.('DePseudoStatsPanel.truncated', 'Zrezané (príliš veľké)') ?? 'Zrezané (príliš veľké)'}
              variant="outlined"
            />
          )}
        </Stack>

        <Divider />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box>
            <Typography variant="body2">
              <strong>{t?.('DePseudoStatsPanel.mappingTotal', 'Záznamy v mape') ?? 'Záznamy v mape'}:</strong>{' '}
              {Number.isFinite(mappingTotal) ? mappingTotal : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>{t?.('DePseudoStatsPanel.tokensFound', 'Nájdené tokeny') ?? 'Nájdené tokeny'}:</strong>{' '}
              {tokensFound ?? '—'}
              {mappingTotal > 0 ? ` / ${mappingTotal}` : ''}
              {integrity ? ` (${integrity})` : ''}
            </Typography>
            <Typography variant="body2">
              <strong>{t?.('DePseudoStatsPanel.tokensNotFound', 'Nenájdené tokeny') ?? 'Nenájdené tokeny'}:</strong>{' '}
              {tokensNotFound ?? '—'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2">
              <strong>{t?.('DePseudoStatsPanel.occFound', 'Nájdené výskyty') ?? 'Nájdené výskyty'}:</strong>{' '}
              {occFound ?? '—'}
            </Typography>
            <Typography variant="body2">
              <strong>{t?.('DePseudoStatsPanel.occReplaced', 'Nahradené výskyty') ?? 'Nahradené výskyty'}:</strong>{' '}
              {occReplaced ?? '—'}
            </Typography>
          </Box>
        </Stack>

        {Array.isArray(notFoundTokens) && notFoundTokens.length > 0 && (
          <>
            <Button size="small" onClick={() => setShowDetails((s) => !s)} sx={{ alignSelf: 'flex-start' }}>
              {showDetails
                ? t?.('DePseudoStatsPanel.hideDetails', 'Skryť detaily') ?? 'Skryť detaily'
                : t?.('DePseudoStatsPanel.showDetails', 'Zobraziť detaily') ?? 'Zobraziť detaily'}
            </Button>

            <Collapse in={showDetails}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t?.('DePseudoStatsPanel.notFoundList', 'Nenájdené tokeny') ?? 'Nenájdené tokeny'}:</strong>
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {notFoundTokens.slice(0, 60).map((tok) => (
                  <Chip key={tok} size="small" label={tok} />
                ))}
                {notFoundTokens.length > 60 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`+${notFoundTokens.length - 60} ${t?.('DePseudoStatsPanel.more', 'ďalších') ?? 'ďalších'}`}
                  />
                )}
              </Stack>
            </Collapse>
          </>
        )}
      </Stack>
    </Alert>
  );
};

export default DePseudoStatsPanel;